import { useContext } from 'react';

import { OptimizelyDecideOption, useDecision } from '@optimizely/react-sdk';

import { UserContext } from 'Context/user';

import { FLAG } from 'Utilities/analytics/optimizely/constants';
import { isUndefined } from 'Utilities/helpers';
import { useFeatureFlags } from 'Utilities/hooks';

import { APIResponseStatus } from 'Services/api.types';

import { useSaleDetailsSub } from '../vehicleCollection/hooks/useSaleDetailsSub';
import { FAMILY_KEY, SELLER } from '../vehicleProfiling/VehicleProfiling.constants';

import { DocumentCaptureConfirmationProgressContext, DocumentCaptureOfferContext } from './DocumentCaptureContexts';

const DISABLE_DECISION = [OptimizelyDecideOption.DISABLE_DECISION_EVENT];

export const useDocumentCaptureExperimentDecisions = () => {
  const { incodeEnabled } = useFeatureFlags();
  const { userState: user } = useContext(UserContext);
  const { data } = useContext(DocumentCaptureOfferContext) || {};

  const {
    progress: {
      bankDetailsAdded,
      bankDetailsReviewFailed,
      documents,
    },
  } = useContext(DocumentCaptureConfirmationProgressContext) ?? {};

  const { hasNegativeEquity, offerStatus } = useSaleDetailsSub();

  const hasSomeDocs = documents?.some(({ uploaded }) => (!!uploaded));
  const hasValidBankDetails = bankDetailsAdded === true && !bankDetailsReviewFailed;
  const isOnFinance = data?.finance?.isVehicleOnFinance;
  const isDataLoaded = !isUndefined(data);

  // keeper data
  const keeperType = data?.seller?.keeperType;
  const isSellerKeeper = keeperType === SELLER;
  const isFamilyOrFriendKeeper = keeperType === FAMILY_KEY;

  const shouldFireDecision = isOnFinance && isDataLoaded;

  const [{ enabled: isFinanceFlagEnabled, variationKey: financeVariationKey }, financeClientReady] = useDecision(
    FLAG.INCODE_SDK_FINANCE,
    { decideOptions: shouldFireDecision ? [] : DISABLE_DECISION },
    { overrideUserId: user?.dpSellerId?.toString() ?? '' },
  );

  const shouldFireKeeperAuthDecision = isFamilyOrFriendKeeper && isDataLoaded;
  const [
    { enabled: isKeeperAuthFlagEnabled, variationKey: keeperAuthVariationKey },
    keeperAuthClientReady,
  ] = useDecision(
    FLAG.INCODE_KEEPER_AUTHORIZATION,
    { decideOptions: shouldFireKeeperAuthDecision ? [] : DISABLE_DECISION },
    { overrideUserId: user?.dpSellerId?.toString() ?? '' },
  );

  const inFinanceExperiment = financeClientReady && isFinanceFlagEnabled && financeVariationKey === 'on' && isOnFinance;
  const isFinanceAllowedOrHasNoFinance = !isOnFinance || inFinanceExperiment;

  const inKeeperAuthExperiment = keeperAuthClientReady
    && isKeeperAuthFlagEnabled
    && keeperAuthVariationKey === 'on'
    && isFamilyOrFriendKeeper;
  const isKeeperTypeAllowed = isSellerKeeper || inKeeperAuthExperiment;

  if (isUndefined(data) || isUndefined(documents) || offerStatus !== APIResponseStatus.SUCCESS) {
    return {
      isIncodeEligible: false,
      isMandatoryBankDetailsEligible: false,
    };
  }

  const isIncodeEligible = incodeEnabled
    && isKeeperTypeAllowed
    && !hasSomeDocs
    && hasValidBankDetails
    && isFinanceAllowedOrHasNoFinance;

  const isMandatoryBankDetailsEligible = [
    !hasSomeDocs,
    !hasValidBankDetails,
    !hasNegativeEquity,
  ].every(Boolean);

  return {
    isIncodeEligible,
    isMandatoryBankDetailsEligible,
  };
};
